import { ActionTypes } from "../../../utils/Constants";
import { IPayloadWrapper } from "../types/PayloadWrapper";
import { IErrorModel } from "../../interfaces/Error/IErrorModel";

import {
  SET_APP_MODE,
  SET_NEXT_ACTION,
  SET_SHOW_LOADER,
  SET_ERROR,
  SET_LOADING,
  ApplicationStateTypes,
  ApplicationState,
  CLEAR_ERROR,
  SET_FORCE_SHELF,
  SET_WAS_CANCELLED,
  CLEAR_APP_STATE,
  SET_LOGIN_STATUS,
  PersistedDataTypes,
  SET_PERSISTED_CART_ID,
  SET_PERSISTED_SORT_ID,
  PersistedState,
  SET_DISPATCH_TIMESTAMP,
  ApplicationMode,
  SET_SHOULD_REDIRECT,
  SET_IS_BETA_WORKSTATION,
  SET_BURNED_ORDERS_MUST_BE_PACKED_FIRST,
  SET_CONVEYOR_ID,
  SET_IS_SCAN_ID_VALIDATION_RX_REQUIRED,
  SET_IS_SCAN_ID_VALIDATION_REMOVE_SKU_REQUIRED,
  SET_IS_SCAN_ID_VALIDATION_CANCEL_SORTBOX_REQUIRED,
  SET_PACKING_ALLOWED,
} from "../types/ApplicationStateTypes";

/**
 * -------------------------------------
 * Module specific constants.
 * -------------------------------------
 * */
// const AppModes = {
//     FETCH_BOX: 0,
//     CHANGE_CART: 1,
//     SEARCH_BOX: 2
// };

/**
 * -------------------------------------
 * Action Creator definitions
 * -------------------------------------
 * */
export const setAppMode = (payload: IPayloadWrapper<ApplicationMode>): ApplicationStateTypes => {
  return {
    type: SET_APP_MODE,
    payload,
  };
};

export const setNextAction = (payload: IPayloadWrapper<number>): ApplicationStateTypes => {
  return {
    type: SET_NEXT_ACTION,
    payload: payload,
  };
};

export const setLoadingStatus = (payload: IPayloadWrapper<boolean>): ApplicationStateTypes => {
  return {
    type: SET_LOADING,
    payload,
  };
};

export const setShowLoader = (payload: IPayloadWrapper<boolean>): ApplicationStateTypes => {
  return {
    type: SET_SHOW_LOADER,
    payload,
  };
};

export const setError = (payload: IErrorModel): ApplicationStateTypes => {
  return {
    type: SET_ERROR,
    payload,
  };
};

export const clearError = (): ApplicationStateTypes => {
  return {
    type: CLEAR_ERROR,
  };
};

export const setForceShelf = (payload: IPayloadWrapper<number>): ApplicationStateTypes => {
  return {
    type: SET_FORCE_SHELF,
    payload,
  };
};

export const setWasCancelled = (payload: IPayloadWrapper<boolean>): ApplicationStateTypes => {
  return {
    type: SET_WAS_CANCELLED,
    payload,
  };
};

export const clearAppState = (): ApplicationStateTypes => {
  return {
    type: CLEAR_APP_STATE,
  };
};

export const setLoginStatus = (payload: IPayloadWrapper<boolean>): ApplicationStateTypes => {
  return {
    type: SET_LOGIN_STATUS,
    payload,
  };
};

export const setPackingAllowed = (payload: IPayloadWrapper<boolean>): ApplicationStateTypes => {
  return {
    type: SET_PACKING_ALLOWED,
    payload,
  };
};

export const setPersistedCartID = (payload: IPayloadWrapper<number>): PersistedDataTypes => {
  return {
    type: SET_PERSISTED_CART_ID,
    payload,
  };
};

export const setPersistedSortID = (payload: IPayloadWrapper<number>): PersistedDataTypes => {
  return {
    type: SET_PERSISTED_SORT_ID,
    payload,
  };
};

export const setShouldRedirect = (payload: IPayloadWrapper<boolean>): ApplicationStateTypes => {
  return {
    type: SET_SHOULD_REDIRECT,
    payload,
  };
};

export const setLatestDispatchTimestamp = (
  payload: IPayloadWrapper<number>
): ApplicationStateTypes => {
  return {
    type: SET_DISPATCH_TIMESTAMP,
    payload,
  };
};

export const setIsBetaWorkstation = (payload: IPayloadWrapper<boolean>): ApplicationStateTypes => {
  return {
    type: SET_IS_BETA_WORKSTATION,
    payload,
  };
};

export const setBurnOrdersMustBeBackedFirst = (
  payload: IPayloadWrapper<boolean>
): ApplicationStateTypes => {
  return {
    type: SET_BURNED_ORDERS_MUST_BE_PACKED_FIRST,
    payload,
  };
};

export const setConveyorID = (payload: IPayloadWrapper<string>): ApplicationStateTypes => {
  return {
    type: SET_CONVEYOR_ID,
    payload,
  };
};

export const setIsScanIdValidationRxRequired = (
  payload: IPayloadWrapper<boolean>
): ApplicationStateTypes => {
  return {
    type: SET_IS_SCAN_ID_VALIDATION_RX_REQUIRED,
    payload,
  };
};

export const setIsScanIdValidationRemoveSkuRequired = (
  payload: IPayloadWrapper<boolean>
): ApplicationStateTypes => {
  return {
    type: SET_IS_SCAN_ID_VALIDATION_REMOVE_SKU_REQUIRED,
    payload,
  };
};

export const setIsScanIdValidationCancelSortboxRequired = (
  payload: IPayloadWrapper<boolean>
): ApplicationStateTypes => {
  return {
    type: SET_IS_SCAN_ID_VALIDATION_CANCEL_SORTBOX_REQUIRED,
    payload,
  };
};

const initialPersistedState: PersistedState = {
  cartID: -1,
  sortID: -1,
};

const initialApplicationState: ApplicationState = {
  nextAction: ActionTypes.REQUEST_CART, //not in use yet
  loading: false,
  showLoader: false,
  error: false,
  visible: false,
  errorMessage: "",
  errorType: -1,
  wasCancelled: false,
  forceShelf: -1, //not in use yet
  loggedIn: false,
  appMode: ApplicationMode.Undefined,
  latestDispatch: new Date(1970, 0, 1).getMilliseconds(),
  shouldRedirect: false,
  isBetaWorkstation: false,
  burnedOrdersMustBePackedFirst: false,
  conveyorID: "",
  isScanIdValidationRxRequired: false,
  isScanIdValidationRemoveSkuRequired: false,
  isScanIdValidationCancelSortboxRequired: false,
  packingAllowed: true,
};

const persistedStateReducer = (
  state = initialPersistedState,
  action: PersistedDataTypes
): PersistedState => {
  switch (action.type) {
    case SET_PERSISTED_SORT_ID:
      return { ...state, sortID: action.payload.value };
    case SET_PERSISTED_CART_ID:
      return { ...state, cartID: action.payload.value };
    default:
      return state;
  }
};

/**
 * -------------------------------------
 * Reducer definition
 * -------------------------------------
 * */
const applicationStateReducer = (
  state = initialApplicationState,
  action: ApplicationStateTypes
): ApplicationState => {
  console.log(action);
  switch (action.type) {
    case SET_SHOULD_REDIRECT:
      return { ...state, shouldRedirect: action.payload.value };
    case SET_APP_MODE:
      return { ...state, appMode: action.payload.value };
    case SET_NEXT_ACTION:
      return { ...state, nextAction: action.payload.value };
    case SET_LOADING:
      return { ...state, loading: action.payload.value };
    case SET_SHOW_LOADER:
      return { ...state, showLoader: action.payload.value };
    case SET_ERROR:
      return {
        ...state,
        errorMessage: action.payload.message,
        visible: action.payload.visible,
        errorType: action.payload.errorType,
        error: true,
      };
    case CLEAR_ERROR:
      return { ...state, errorMessage: "", error: false, visible: false, errorType: -1 };
    case SET_WAS_CANCELLED:
      return { ...state, wasCancelled: action.payload.value };
    case SET_FORCE_SHELF:
      return { ...state, forceShelf: action.payload.value };
    case CLEAR_APP_STATE:
      return { ...state, ...initialApplicationState };
    case SET_LOGIN_STATUS:
      return { ...state, loggedIn: action.payload.value };
    case SET_PACKING_ALLOWED:
      return { ...state, packingAllowed: action.payload.value };
    case SET_DISPATCH_TIMESTAMP:
      return { ...state, latestDispatch: action.payload.value };
    case SET_IS_BETA_WORKSTATION:
      return { ...state, isBetaWorkstation: action.payload.value };
    case SET_BURNED_ORDERS_MUST_BE_PACKED_FIRST:
      return { ...state, burnedOrdersMustBePackedFirst: action.payload.value };
    case SET_CONVEYOR_ID:
      return { ...state, conveyorID: action.payload.value };
    case SET_IS_SCAN_ID_VALIDATION_RX_REQUIRED:
      return { ...state, isScanIdValidationRxRequired: action.payload.value };
    case SET_IS_SCAN_ID_VALIDATION_REMOVE_SKU_REQUIRED:
      return { ...state, isScanIdValidationRemoveSkuRequired: action.payload.value };
    case SET_IS_SCAN_ID_VALIDATION_CANCEL_SORTBOX_REQUIRED:
      return { ...state, isScanIdValidationCancelSortboxRequired: action.payload.value };
    default:
      return state;
  }
};

/**
 * -------------------------------------
 * Public interface
 * -------------------------------------
 * */
export { applicationStateReducer, persistedStateReducer };
